import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { companyData, logoUrl } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileYdelserOpen, setIsMobileYdelserOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Forside', href: '/' },
    { label: 'Om os', href: '/#om-os' },
    { label: 'Ydelser', href: '/#ydelser' },
    { label: 'Galleri', href: '/galleri' },
    { label: 'Kontakt', href: '/kontakt' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);

    if (href === '/kontakt' || href === '/galleri') {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (location.pathname === '/') {
      const targetId = href === '/' ? '#forside' : href.replace('/', '');
      const element = document.querySelector(targetId);
      if (element) {
        const offset = 80; // height of sticky header
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(href);
    }
  };

  const handleServiceItemClick = (e, serviceId) => {
    e.preventDefault();
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileYdelserOpen(false);
    navigate(`/ydelser/${serviceId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-gray-200 shadow-md ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center gap-3 group">
            <img
              src={logoUrl}
              alt={companyData.name}
              className={`w-auto object-contain transition-all duration-300 group-hover:scale-105 ${
                isScrolled ? 'h-13 md:h-18' : 'h-16 md:h-24'
              }`}
            />
            <span className="sr-only">{companyData.name}</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => {
              if (item.label === 'Ydelser') {
                return (
                  <div
                    key={item.href}
                    className="relative"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setIsDropdownOpen(!isDropdownOpen);
                      }}
                      className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-black transition-colors duration-200 uppercase tracking-wider focus:outline-none cursor-pointer py-2"
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div
                      className={`absolute left-0 mt-1 w-64 bg-white border border-gray-200 shadow-xl rounded-none py-1 transition-all duration-200 origin-top-left ${
                        isDropdownOpen
                          ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                      }`}
                    >
                      {companyData.services.map((service) => (
                        <a
                          key={service.id}
                          href="#ydelser"
                          onClick={(e) => handleServiceItemClick(e, service.id)}
                          className="block px-5 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                        >
                          <div className="font-bold text-gray-900 text-sm">{service.title}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="text-sm font-semibold text-gray-700 hover:text-black transition-colors duration-200 uppercase tracking-wider py-2"
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${companyData.phoneRaw}`}
              className="flex items-center gap-2 text-gray-700 hover:text-black transition-colors duration-200 text-sm font-semibold"
            >
              <Phone className="w-4 h-4 text-black" fill="currentColor" />
              <span>{companyData.phone}</span>
            </a>
            <a
              href="/kontakt"
              onClick={(e) => handleNavClick(e, '/kontakt')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-none text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
            >
              Få et tilbud
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-4">
            <a
              href={`tel:${companyData.phoneRaw}`}
              className="p-2 text-gray-700 hover:text-black transition-colors"
              aria-label="Ring til os"
            >
              <Phone className="w-5 h-5 text-black" fill="currentColor" />
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gray-700 hover:text-black transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`md:hidden fixed inset-x-0 transition-all duration-300 ease-in-out bg-white border-b border-gray-200 ${
          isScrolled ? 'top-[73px]' : 'top-[113px]'
        } ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="px-4 pt-4 pb-6 space-y-3 shadow-xl max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => {
            if (item.label === 'Ydelser') {
              return (
                <div key={item.href} className="space-y-1">
                  <button
                    onClick={() => setIsMobileYdelserOpen(!isMobileYdelserOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-base font-semibold text-gray-700 hover:text-black hover:bg-gray-50 rounded-md transition-all duration-200 uppercase tracking-wider focus:outline-none"
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isMobileYdelserOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isMobileYdelserOpen && (
                    <div className="pl-6 space-y-1 bg-gray-50 py-1 border-l-2 border-orange-500">
                      {companyData.services.map((service) => (
                        <a
                          key={service.id}
                          href="#ydelser"
                          onClick={(e) => handleServiceItemClick(e, service.id)}
                          className="block px-4 py-2.5 text-sm font-semibold text-gray-600 hover:text-black hover:bg-gray-100 transition-all duration-200"
                        >
                          {service.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:text-black hover:bg-gray-50 rounded-md transition-all duration-200 uppercase tracking-wider"
              >
                {item.label}
              </a>
            );
          })}
          
          <div className="pt-4 border-t border-gray-100 px-4 flex flex-col gap-4">
            <a
              href={`tel:${companyData.phoneRaw}`}
              className="flex items-center gap-3 text-gray-700 hover:text-black transition-colors text-base font-bold"
            >
              <Phone className="w-5 h-5 text-black" fill="currentColor" />
              <span>{companyData.phone}</span>
            </a>
            <a
              href={`mailto:${companyData.email}`}
              className="flex items-center gap-3 text-gray-700 hover:text-black transition-colors text-base font-bold"
            >
              <Mail className="w-5 h-5 text-orange-500" />
              <span>{companyData.email}</span>
            </a>
            <a
              href="/kontakt"
              onClick={(e) => handleNavClick(e, '/kontakt')}
              className="w-full text-center bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-none text-base font-bold uppercase tracking-wider transition-all duration-200"
            >
              Få et uforpligtende tilbud
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
